import React from 'react';
import { Link } from 'react-router-dom';
import { isEnglish } from '../i18n/locale';
import usePageMetadata from '../hooks/usePageMetadata';
import { TuttiHeader, TuttiFooter, TuttiDownload, TuttiStoreInstall } from '../components/TuttiHelperChrome';
import { TUTTI_HELPER as app } from '../data/tuttiHelper';
import chineseStory from '../data/tuttiStory.zh.json';
import englishStory from '../data/tuttiStory.en.json';
import { TuttiPreview } from './TuttiHelper';
import storyImages from '../data/tuttiStoryImages.json';

function InlineLinks({ text }) {
  return text.split(/(\[[^\]]+\]\([^)]+\))/g).map((part, index) => {
    const match = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (!match) return <React.Fragment key={index}>{part}</React.Fragment>;
    const path = match[2].replace('https://peacetech.app', '');
    return path.startsWith('/') ? <Link key={index} to={path}>{match[1]}</Link> : <a key={index} href={path} target="_blank" rel="noopener noreferrer">{match[1]}</a>;
  });
}

function StoryFigure({ imageId, cover = false }) {
  if (imageId === 'cover') return <figure className="th-story-cover th-story-free-cover"><p>PEACETECH / TUTTI ACTIVITY HELPER</p><h2>{isEnglish ? 'A little less refreshing. A little more creating.' : '少一点刷新。多一点创作。'}</h2><div><span>{isEnglish ? 'All features free' : '全部功能免费'}</span><span>{isEnglish ? '1 min checks · Alerts · Coin sound' : '1 分钟检查 · 新活动提醒 · 金币提示音'}</span></div><span className="th-story-cover-mark" aria-hidden="true">✳</span></figure>;
  if (imageId === 'plugin') return <TuttiPreview />;
  if (imageId === 'website') return <figure className="th-story-figure th-story-install-preview"><span>CHROME / ZIP v{app.version}</span><h3>{isEnglish ? 'Everything is ready in your toolbar.' : '把全部功能，装进你的工具栏。'}</h3><ol><li>{isEnglish ? 'Download and unzip' : '下载并解压'}</li><li>{isEnglish ? 'Load unpacked in Chrome' : 'Chrome 加载未打包扩展'}</li><li>{isEnglish ? 'Sign in to Tutti and start monitoring' : '登录 Tutti，开启监控'}</li></ol><Link to={`${app.path}?lang=${isEnglish ? 'en' : 'zh'}#install`}>{isEnglish ? 'Open the current installation guide ↗' : '打开最新安装说明 ↗'}</Link></figure>;

  const image = storyImages[imageId];
  const language = isEnglish ? 'en' : 'zh';
  const visual = image.localized?.[language] || image;
  return <figure className={cover ? 'th-story-cover' : 'th-story-figure'}>
    <a href={visual.src} target="_blank" rel="noopener noreferrer" aria-label={isEnglish ? `Open full image: ${image.alt[language]}` : `查看原图：${image.alt[language]}`}>
      <img src={visual.src} alt={image.alt[language]} width={visual.width} height={visual.height} loading={cover ? 'eager' : 'lazy'} decoding="async" />
    </a>
    {!cover && <figcaption>{image.caption[language]}{isEnglish && visual.translated && <> <a href={image.src} target="_blank" rel="noopener noreferrer">View original Chinese screenshot ↗</a></>}</figcaption>}
  </figure>;
}

export default function TuttiHelperStory() {
  const story = isEnglish ? englishStory : chineseStory;
  usePageMetadata(`${story.title} · PeaceTech`, isEnglish ? 'Why I built a small Chrome extension for Tutti campaigns, what it does, and how to get started.' : '为什么做 Tutti 活动助手，如何安装使用，以及第一版的功能与边界。', app.storyPath, 'th-page-active');
  return <div className="th-site">
    <a className="th-skip" href="#th-content">{isEnglish ? 'Skip to story' : '跳到正文'}</a>
    <TuttiHeader />
    <main id="th-content" className="th-editorial">
      <a className="th-article-invite" href={app.referralUrl} target="_blank" rel="noopener noreferrer">{isEnglish ? 'New to Tutti? Join with my referral link · QMX2H4 ↗' : '还没有 Tutti 账号？从我的邀请链接进入 · QMX2H4 ↗'}</a>
      <header className="th-article-header">
        <p className="th-eyebrow">{isEnglish ? 'MAKER’S NOTES / 001' : '开发手记 / 001'}</p>
        <h1>{story.title}</h1>
        <p className="th-article-meta">PeaceTech · <time dateTime="2026-10-05">2026.10.05</time> · v{app.version}</p>
      </header>
      <StoryFigure imageId="cover" cover />
      <div className="th-article-layout">
        <article className="th-prose">
          {story.blocks.map((block, index) => {
            if (block.type === 'h2') return <h2 key={index} id={block.id}>{block.text}</h2>;
            if (block.type === 'image') return <StoryFigure key={index} imageId={block.imageId} />;
            return <p key={index} className={index === 0 ? 'th-article-lead' : undefined}><InlineLinks text={block.text} /></p>;
          })}
          <div className="th-story-end"><TuttiStoreInstall /><TuttiDownload placement="story_end" /><p><a href={`/downloads/tutti-activity-helper-launch.${isEnglish ? 'en' : 'zh'}.md`} download>{isEnglish ? 'Download this article (Markdown)' : '下载这篇文章（Markdown）'}</a> · <Link to={`${app.path}#install`}>{isEnglish ? 'Installation guide' : '查看安装步骤'}</Link></p></div>
        </article>
        <aside className="th-article-aside"><p>{isEnglish ? 'IN THIS STORY' : '这篇文章里'}</p><nav aria-label={isEnglish ? 'Article contents' : '文章目录'}>{story.blocks.filter(block => block.type === 'h2').map(block => <a href={`#${block.id}`} key={block.id}>{block.text}</a>)}</nav><TuttiStoreInstall small /><TuttiDownload placement="story_sidebar" /></aside>
      </div>
    </main>
    <TuttiFooter />
  </div>;
}
